import { createApi } from "@reduxjs/toolkit/query/react";
import { getAdminAxiosInstance } from "../../../api/Admin";

const axiosBaseQuery =
  () =>
  async ({ params, endpoint, method, body }) => {
    const api = await getAdminAxiosInstance();
    const getParams = (key) => {
      if (params?.[key] || params?.[key] === 0) {
        return `${key}=${params[key]}&`;
      } else {
        return "";
      }
    };
    if (method === "get") {
      try {
        const response = await api[method](
          `${endpoint}?${getParams("plan_id")}${getParams(
            "offer_id"
          )}${getParams("subscription_id")}${getParams("user_id")}${getParams(
            "customer_key"
          )}${getParams("status")}${getParams("audience")}${getParams("type")}${getParams(
            "group_product_id"
          )}${getParams(
            "start_date"
          )}${getParams("end_date")}${getParams("search")}${getParams(
            "sort"
          )}${getParams("sort_order")}${getParams("page")}${
            params?.page_size ? `per_page=${params.page_size}&` : ""
          }`,
          body
        );
        return { data: response.data };
      } catch (axiosError) {
        let err = axiosError;
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
    } else {
      try {
        const response = await api[method](`${endpoint}`, body);
        return { data: response.data };
      } catch (axiosError) {
        let err = axiosError;
        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        };
      }
    }
  };

export const subscription = createApi({
  reducerPath: "subscriptionApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    "Subscription Plans",
    "Corporate Offers",
    "Customer Subscriptions",
    "Subscription Notification",
  ],
  endpoints: (builder) => ({
    getSubscriptionPlanListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/subscription-plan/list",
      }),
      providesTags: ["Subscription Plans"],
    }),
    updateSubscriptionPlanTableHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-field/store`,
      }),
      invalidatesTags: ["Subscription Plans"],
    }),

    /**
     * Called bare this is the form's setup - statuses, line types, max_lines and the outlet
     * picker. Products are deliberately absent: menus are held per outlet, so the picker has
     * to be answered by getSubscriptionPlanProducts once an outlet is chosen.
     */
    getSubscriptionPlanBasicData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/subscription-plan/basic-data",
      }),
    }),

    /**
     * Same endpoint, second mode: with a customer_key it answers with that outlet's product
     * list and nothing else. An unknown key gives an empty list rather than falling back to
     * another outlet's menu, which would let a plan be built from items the store cannot sell.
     */
    getSubscriptionPlanProducts: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/subscription-plan/basic-data",
      }),
    }),

    uploadSubscriptionPlanImage: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/subscription-plan/upload-image`,
      }),
    }),

    /**
     * Prices the lines without saving anything, so the builder can show the selling price and
     * each item's redemption price as the team types. A mutation rather than a query because
     * it is fired on edit rather than on render, and its result is never cached.
     */
    getSubscriptionPlanPreview: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/subscription-plan/preview`,
      }),
    }),

    updateSubscriptionPlanStatus: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/subscription-plan/change-status`,
      }),
    }),

    /**
     * Corporate offers. The list speaks the same envelope as the plan list - result,
     * total_count, fields, module_id and the permission flags - so the screen runs on the
     * shared table with saved column preferences, server search and server paging.
     */
    getCorporateOfferListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/corporate-offer/list",
      }),
      providesTags: ["Corporate Offers"],
    }),
    updateCorporateOfferTableHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-field/store`,
      }),
      invalidatesTags: ["Corporate Offers"],
    }),
    getCorporateOfferDetails: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/corporate-offer/details",
      }),
      providesTags: ["Corporate Offers"],
    }),
    getCorporateOfferPreview: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/corporate-offer/preview`,
      }),
    }),
    activateCorporateOffer: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/corporate-offer/activate`,
      }),
      invalidatesTags: ["Corporate Offers"],
    }),
    deactivateCorporateOffer: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/corporate-offer/deactivate`,
      }),
      invalidatesTags: ["Corporate Offers"],
    }),
    getCorporateOfferCodes: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/corporate-offer/export-codes",
      }),
    }),

    /**
     * Support for subscriptions already sold. Note that none of these send an admin_id - the
     * admin service stamps it from the token, so the dashboard cannot attribute a
     * cancellation or a credit to anyone but the person signed in.
     */
    getCustomerSubscriptionListData: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/customer-subscription/list",
      }),
      providesTags: ["Customer Subscriptions"],
    }),
    updateCustomerSubscriptionTableHeadData: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/user-field/store`,
      }),
      invalidatesTags: ["Customer Subscriptions"],
    }),
    getCustomerSubscriptionDetails: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/customer-subscription/details",
      }),
      providesTags: ["Customer Subscriptions"],
    }),
    cancelCustomerSubscription: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/customer-subscription/cancel`,
      }),
      invalidatesTags: ["Customer Subscriptions"],
    }),
    creditCustomerSubscriptionBalance: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/customer-subscription/credit-balance`,
      }),
      invalidatesTags: ["Customer Subscriptions"],
    }),
    getSubscriptionNotificationSetting: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/customer-subscription/notification-setting",
      }),
      providesTags: ["Subscription Notification"],
    }),
    saveSubscriptionNotificationSetting: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/customer-subscription/save-notification-setting`,
      }),
      invalidatesTags: ["Subscription Notification"],
    }),

    /**
     * Reporting. The three headline endpoints are POSTs that take the filters in the body,
     * so they are mutations here even though nothing is written - RTK Query has no notion of
     * a query with a request body.
     */
    getSubscriptionReportSummary: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/subscription-report/summary`,
      }),
    }),
    getSubscriptionPlanBreakdown: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/subscription-report/plan-breakdown`,
      }),
    }),
    getSubscriptionOutletBreakdown: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/subscription-report/outlet-breakdown`,
      }),
    }),
    getSubscriptionRedemptionList: builder.query({
      query: (values) => ({
        method: "get",
        params: values,
        endpoint: "/subscription-report/redemption-list",
      }),
    }),
    exportSubscriptionReport: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/subscription-report/export`,
      }),
    }),
    exportSubscriptionRedemptions: builder.mutation({
      query: (body) => ({
        method: "post",
        body,
        endpoint: `/subscription-report/redemption-export`,
      }),
    }),
  }),
});

export const {
  useGetSubscriptionPlanListDataQuery,
  useUpdateSubscriptionPlanTableHeadDataMutation,
  useGetSubscriptionPlanBasicDataQuery,
  useGetSubscriptionPlanProductsQuery,
  useUploadSubscriptionPlanImageMutation,
  useGetSubscriptionPlanPreviewMutation,
  useUpdateSubscriptionPlanStatusMutation,
  useGetCorporateOfferListDataQuery,
  useUpdateCorporateOfferTableHeadDataMutation,
  useGetCorporateOfferDetailsQuery,
  useGetCorporateOfferPreviewMutation,
  useActivateCorporateOfferMutation,
  useDeactivateCorporateOfferMutation,
  useLazyGetCorporateOfferCodesQuery,
  useGetCustomerSubscriptionListDataQuery,
  useUpdateCustomerSubscriptionTableHeadDataMutation,
  useGetCustomerSubscriptionDetailsQuery,
  useCancelCustomerSubscriptionMutation,
  useCreditCustomerSubscriptionBalanceMutation,
  useGetSubscriptionNotificationSettingQuery,
  useSaveSubscriptionNotificationSettingMutation,
  useGetSubscriptionReportSummaryMutation,
  useGetSubscriptionPlanBreakdownMutation,
  useGetSubscriptionOutletBreakdownMutation,
  useGetSubscriptionRedemptionListQuery,
  useExportSubscriptionReportMutation,
  useExportSubscriptionRedemptionsMutation,
} = subscription;
